import { Box, TextField, IconButton, Tooltip, Chip } from "@mui/material";
import {
    Edit as EditIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FieldConfig } from "./TaxReturnWebforms/types/formConfig";

interface ConfigEditorFieldProps {
    field: FieldConfig;
    fieldId: string;
    onEdit: (field: FieldConfig) => void;
    onToggleImportant: () => void;
    isDragging?: boolean;
    disableDrag?: boolean;
}

const ConfigEditorField = ({
    field,
    fieldId,
    onEdit,
    onToggleImportant,
    isDragging = false,
    disableDrag = false,
}: ConfigEditorFieldProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSortableDragging,
    } = useSortable({ id: fieldId });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isSortableDragging ? 0.5 : 1,
    };

    const dragging = isDragging || isSortableDragging;

    // Detect when the metadata row wraps — once wrapped, right-align every line
    // so the chips (previous line) shift to the end instead of sitting at the start.
    const metaRowRef = useRef<HTMLDivElement>(null);
    const [isWrapped, setIsWrapped] = useState(false);

    useEffect(() => {
        const el = metaRowRef.current;
        if (!el) return undefined;

        const check = () => {
            const children = Array.from(el.children) as HTMLElement[];
            if (children.length < 2) {
                setIsWrapped(false);
                return;
            }
            const firstTop = children[0].offsetTop;
            setIsWrapped(children.some((c) => c.offsetTop !== firstTop));
        };

        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [field.type, field.colSpan, field.important]);

    return (
        <Box
            ref={setNodeRef}
            style={style}
            sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                minWidth: 0,
            }}
        >
            {/* Input row: drag handle vertically centered with the TextField */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                {!disableDrag && (
                    <Tooltip title="Drag to reorder">
                        <IconButton
                            size="small"
                            {...attributes}
                            {...listeners}
                            sx={{
                                cursor: dragging ? "grabbing" : "grab",
                                p: 0.25,
                                opacity: 0.6,
                                "&:hover": {
                                    opacity: 1,
                                },
                            }}
                        >
                            <DragIndicatorIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip
                    title={(
                        <Box sx={{ fontSize: "0.9rem", lineHeight: 1.4 }}>
                            <Box sx={{ fontWeight: 600, mb: 0.5 }}>{field.label}</Box>
                            <Box sx={{ fontFamily: "monospace", opacity: 0.85 }}>{field.fieldPath}</Box>
                        </Box>
                    )}
                    placement="top"
                    enterDelay={300}
                    slotProps={{
                        tooltip: {
                            sx: {
                                fontSize: "0.9rem",
                                maxWidth: 500,
                                p: 1.5,
                            },
                        },
                    }}
                >
                    <TextField
                        label={field.label}
                        value={field.fieldPath}
                        size="small"
                        fullWidth
                        disabled
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                bgcolor: "background.paper",
                                ...(field.important && {
                                    "& .MuiOutlinedInput-notchedOutline, &.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "warning.main",
                                        borderWidth: 2,
                                    },
                                }),
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: field.important ? "warning.dark" : "primary.main",
                                },
                            },
                            "& .MuiInputLabel-root": {
                                fontWeight: field.important ? 600 : 400,
                                maxWidth: "calc(100% - 24px)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                ...(field.important && {
                                    color: "warning.main",
                                }),
                            },
                            "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "inherit",
                                fontFamily: "monospace",
                                fontSize: "0.85rem",
                                textOverflow: "ellipsis",
                            },
                        }}
                    />
                </Tooltip>
            </Box>
            <Box
                ref={metaRowRef}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexWrap: "wrap",
                    ...(isWrapped && { justifyContent: "flex-end" }),
                }}
            >
                {field.type && (
                    <Chip
                        label={field.type}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.65rem" }}
                    />
                )}
                {field.colSpan && (
                    <Chip
                        label={`span:${field.colSpan}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.65rem" }}
                    />
                )}
                <Box sx={{ display: "flex", alignItems: "center", ml: isWrapped ? 0 : "auto" }}>
                    <Tooltip title={field.important ? "Remove important" : "Mark as important"}>
                        <IconButton
                            size="small"
                            onClick={onToggleImportant}
                            sx={{ p: 0.25 }}
                        >
                            {field.important ? (
                                <StarIcon fontSize="small" color="warning" />
                            ) : (
                                <StarBorderIcon fontSize="small" color="action" />
                            )}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit field">
                        <IconButton
                            size="small"
                            onClick={() => onEdit(field)}
                            sx={{ p: 0.25 }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
};

export default ConfigEditorField;
